import moment from 'moment';
import { COLUMNS, DICT_FIELDS, VALUE_CURRENCY_FIELD, CSV_DATE_HEADER, CSV_VALUE_HEADER } from './consts';

// Client-only unique key so React can track rows (including brand-new, id-less rows) across edits.
let rowKeyCounter = 0;
export const nextRowKey = () => {
    rowKeyCounter += 1;
    return `row_${rowKeyCounter}`;
};

// Normalize a server row (from get_table) into the flat shape the grid edits in place.
// The backend nests the date under `start_date: {startDate, endDate}`; everything else is
// already a `{name, display_name}` dict object (or null).
export const normalizeRow = serverRow => {
    const row = {
        _key: nextRowKey(),
        id: serverRow.id ?? null,
        startDate: serverRow.start_date ? serverRow.start_date.startDate || null : null,
        value: serverRow.value === null || serverRow.value === undefined ? '' : `${serverRow.value}`,
    };

    DICT_FIELDS.forEach(field => {
        row[field] = serverRow[field] || null;
    });

    return row;
};

export const createEmptyRow = (isValueCurrencyEnabled = false) => {
    const row = {
        _key: nextRowKey(),
        id: null,
        startDate: null,
        value: '',
    };

    DICT_FIELDS.forEach(field => {
        row[field] = null;
    });

    if (!isValueCurrencyEnabled) {
        delete row[VALUE_CURRENCY_FIELD];
    }

    return row;
};

// The visible dictionary columns for a table, in display order, from its `visibility_cols`.
export const getVisibleColumns = (visibilityCols = []) => {
    if (!visibilityCols || !visibilityCols.length) {
        return COLUMNS;
    }
    return COLUMNS.filter(column => visibilityCols.includes(column.field));
};

// Empty string / null → null; anything else → Number. Mirrors the legacy "value === 0 || value" test.
export const parseValue = value => {
    if (value === '' || value === null || value === undefined) {
        return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
};

// A stable comparable signature of a row's meaningful content, used to detect unedited rows.
const rowSignature = row => ({
    date: row.startDate || null,
    value: parseValue(row.value),
    fields: DICT_FIELDS.reduce((acc, field) => {
        acc[field] = row[field] ? row[field].name : null;
        return acc;
    }, {}),
});

const isSameRow = (row, originalRow) => {
    if (!originalRow) {
        return false;
    }
    return JSON.stringify(rowSignature(row)) === JSON.stringify(rowSignature(originalRow));
};

// A dict cell is valid when it resolves to an entry in its dictionary (matched by `name`).
// An empty dictionary is treated as "cannot validate" and left alone, to avoid false errors
// for restricted users who only receive a subset of dictionary values.
const isDictCellValid = (cell, dictionary) => {
    if (!cell) {
        return true;
    }
    if (!dictionary || !dictionary.length) {
        return true;
    }
    return dictionary.some(entry => entry.name === cell.name);
};

const isValidDate = value => {
    if (!value) {
        return true;
    }
    return moment(value, 'YYYY-MM-DD', true).isValid();
};

// Build the update payload from the current grid rows.
//
// Parity notes with the legacy Angular controller:
//  - Every row that has a value is sent (the backend deletes any existing row absent from the
//    payload), not just edited rows.
//  - Unedited rows skip cell validation so a restricted user's untouched rows never block a save.
//  - The lone blank starter row (single row, no conditions, no date) is ignored, not flagged.
//
// Returns { payload, errorsByKey, valid, hasInvalidDate }.
export const extractTableData = ({ rows, originalById, dictionaries, isValueCurrencyEnabled }) => {
    const payload = [];
    const errorsByKey = {};
    let valid = true;
    let hasInvalidDate = false;

    const totalRows = rows.length;

    rows.forEach(row => {
        const errors = {};
        let hasConditions = false;

        const original = row.id !== null && row.id !== undefined ? originalById[row.id] : null;
        const unedited = isSameRow(row, original);

        if (!unedited) {
            DICT_FIELDS.forEach(field => {
                if (!isValueCurrencyEnabled && field === VALUE_CURRENCY_FIELD) {
                    return;
                }
                if (row[field]) {
                    if (isDictCellValid(row[field], dictionaries[field])) {
                        hasConditions = true;
                    } else {
                        errors[field] = true;
                    }
                }
            });
        }

        const isEmptyStarterRow = totalRows === 1 && !hasConditions && !row.startDate;

        if (isValueCurrencyEnabled && !row[VALUE_CURRENCY_FIELD] && !isEmptyStarterRow) {
            errors[VALUE_CURRENCY_FIELD] = true;
        }

        if (!isValidDate(row.startDate)) {
            hasInvalidDate = true;
        }

        const numericValue = parseValue(row.value);
        const hasValue = numericValue === 0 || Boolean(numericValue);

        if (hasValue) {
            const payloadRow = {
                id: row.id ?? null,
                date: row.startDate || null,
                value: numericValue,
            };
            DICT_FIELDS.forEach(field => {
                payloadRow[field] = row[field] || null;
            });
            payload.push(payloadRow);
        } else if (!isEmptyStarterRow) {
            errors.value = true;
        }

        if (Object.keys(errors).length) {
            errorsByKey[row._key] = errors;
            valid = false;
        }
    });

    return { payload, errorsByKey, valid, hasInvalidDate };
};

// "Always" when no date is set, otherwise the formatted rule start date.
export const formatStartDate = (startDate, alwaysLabel) => {
    if (!startDate) {
        return alwaysLabel;
    }
    return moment(startDate).format('YYYY-MM-DD');
};

const escapeCsvCell = cell => {
    const text = cell === null || cell === undefined ? '' : `${cell}`;
    if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
};

// Serialize the (already filtered) grid rows to CSV, matching the legacy column contract:
// "Rule Start Date", each visible dictionary column (by field key), "value", and optionally
// "value_currency".
export const buildCsv = ({ rows, visibleColumns, isValueCurrencyEnabled }) => {
    const columnKeys = [CSV_DATE_HEADER, ...visibleColumns.map(column => column.field), CSV_VALUE_HEADER];
    if (isValueCurrencyEnabled) {
        columnKeys.push(VALUE_CURRENCY_FIELD);
    }

    const headerLine = columnKeys.map(escapeCsvCell).join(',');

    const dataLines = rows.map(row =>
        columnKeys
            .map(key => {
                if (key === CSV_DATE_HEADER) {
                    return escapeCsvCell(row.startDate || '');
                }
                if (key === CSV_VALUE_HEADER) {
                    return escapeCsvCell(row.value === '' || row.value === null ? '' : row.value);
                }
                return escapeCsvCell(row[key] ? row[key].display_name || '' : '');
            })
            .join(',')
    );

    return [headerLine, ...dataLines].join('\n');
};

// Turn a dictionary of `{name, display_name}` entries into react-select options.
export const toOptions = (dictionary = []) =>
    dictionary.map(entry => ({ value: entry.name, label: entry.display_name }));

// Filter the grid rows by the per-column free-text filters (case-insensitive substring on display_name).
export const applyFilters = (rows, filters) => {
    const activeFilters = Object.entries(filters).filter(([, query]) => query && query.length);
    if (!activeFilters.length) {
        return rows;
    }
    return rows.filter(row =>
        activeFilters.every(([field, query]) => {
            const cell = row[field];
            const display = cell ? cell.display_name || '' : '';
            return display.toLowerCase().includes(query.toLowerCase());
        })
    );
};

export const hasActiveFilters = filters => Object.values(filters).some(query => query && query.length);
